import React, {
  useState,
  useEffect,
  useCallback,
  useImperativeHandle,
} from "react";
import {
  Card,
  Button,
  Space,
  message,
  DatePicker,
  Tooltip,
  Tag,
  Modal,
  Typography,
} from "antd";

const { RangePicker } = DatePicker;

const { Text } = Typography;
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import AgGridWithColumnState from "../../../../components/AgGridWithColumnState";
import {
  RollbackOutlined,
  ReloadOutlined,
  ExclamationCircleOutlined,
  DownloadOutlined,
  EditOutlined,
} from "@ant-design/icons";
import moment from "moment";
import {
  getAllStatementHistory,
  rollbackStatementHistory,
} from "../../../api/accounting";
import {
  getCachedData,
  setCachedData,
  invalidateCacheByPrefix,
  CacheTTL,
} from "../../../context/AccountingDataCache";

const StatementHistory = React.forwardRef(({ selectedBrand = null }, ref) => {
  // All time by default (null = no date filter), like the rest of the module
  const [dateRange, setDateRange] = useState(null);

  // Cache key includes date range when filtered
  const cacheKey = selectedBrand
    ? `statement-history-${selectedBrand.id}-${dateRange?.[0]?.format("YYYY-MM-DD") ?? "all"}-${dateRange?.[1]?.format("YYYY-MM-DD") ?? "all"}`
    : null;

  // Check global cache for initial data
  const initialData = cacheKey ? getCachedData(cacheKey) : null;

  const [historyData, setHistoryData] = useState(() => initialData || []);
  const [loading, setLoading] = useState(() => !initialData);
  const [rollbackLoading, setRollbackLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 25,
    total: initialData?.length || 0,
  });

  const fetchingRef = React.useRef(false);
  const lastFetchedCacheKeyRef = React.useRef(initialData ? cacheKey : null);

  // Fetch all statement history
  const fetchAllHistory = useCallback(
    async (page = 1, pageSize = 25, force = false) => {
      if (!selectedBrand || !cacheKey) {
        setHistoryData([]);
        return;
      }

      // Check GLOBAL cache first if not forcing refresh
      if (!force) {
        const cachedData = getCachedData(cacheKey);
        if (cachedData) {
          setHistoryData(cachedData);
          setPagination((prev) => ({ ...prev, total: cachedData.length }));
          setLoading(false);
          lastFetchedCacheKeyRef.current = cacheKey;
          return;
        }
      }

      // Already fetched for this key
      if (!force && lastFetchedCacheKeyRef.current === cacheKey) {
        setLoading(false);
        return;
      }

      // Prevent duplicate fetches
      if (fetchingRef.current && !force) {
        return;
      }

      fetchingRef.current = true;
      setLoading(true);

      try {
        const filters = {
          brand: selectedBrand.id,
          page: page,
          page_size: pageSize,
        };
        if (dateRange?.[0] && dateRange?.[1]) {
          filters.start_date = dateRange[0].format("YYYY-MM-DD");
          filters.end_date = dateRange[1].format("YYYY-MM-DD");
        }
        const response = await getAllStatementHistory(filters);

        setHistoryData(response.results || []);
        setPagination({
          current: page,
          pageSize: pageSize,
          total: response.count || 0,
        });

        // Cache in GLOBAL cache (5 minute TTL)
        setCachedData(cacheKey, response.results || [], CacheTTL.MEDIUM);
        lastFetchedCacheKeyRef.current = cacheKey;
      } catch (err) {
        console.error("Statement history fetch failed:", err);
        setHistoryData([]);
        setPagination({ current: 1, pageSize: 25, total: 0 });
      } finally {
        setLoading(false);
        fetchingRef.current = false;
      }
    },
    [selectedBrand, cacheKey, dateRange]
  );

  // Export functionality for Statement History
  const exportToExcel = useCallback(
    async (returnFile = false) => {
      try {
        const exportFilters = {
          brand: selectedBrand ? selectedBrand.id : null,
          start_date: dateRange?.[0]?.format("YYYY-MM-DD") || null,
          end_date: dateRange?.[1]?.format("YYYY-MM-DD") || null,
        };

        const response = await fetch(
          "/api/accounting/statements/history/export/",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
            },
            body: JSON.stringify(exportFilters),
          }
        );

        if (!response.ok) {
          throw new Error(`Export failed: ${response.statusText}`);
        }

        const blob = await response.blob();

        if (returnFile) {
          // Return blob for email sharing
          const timestamp = new Date()
            .toISOString()
            .slice(0, 19)
            .replace(/[:-]/g, "");
          const filename = `statement_history_export_${timestamp}.xlsx`;
          return new File([blob], filename, {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          });
        } else {
          // Direct download
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;

          const timestamp = new Date()
            .toISOString()
            .slice(0, 19)
            .replace(/[:-]/g, "");
          link.download = `statement_history_export_${timestamp}.xlsx`;

          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);

          message.success("Statement history exported successfully!");
        }
      } catch (error) {
        message.error(`Export failed: ${error.message}`);
        throw error;
      }
    },
    [selectedBrand, dateRange]
  );

  // Expose export function via ref
  useImperativeHandle(ref, () => ({
    exportToExcel,
  }));

  // Fetch statement history when brand or date range changes
  useEffect(() => {
    if (!selectedBrand || !cacheKey) return;

    // Check GLOBAL cache first - restore immediately if available
    const cachedData = getCachedData(cacheKey);
    if (cachedData) {
      setHistoryData(cachedData);
      setPagination((prev) => ({ ...prev, total: cachedData.length }));
      setLoading(false);
      lastFetchedCacheKeyRef.current = cacheKey;
      return;
    }

    fetchAllHistory(1, 25);
  }, [selectedBrand, cacheKey, dateRange, fetchAllHistory]);

  // AG Grid column definitions for historical data
  const columnDefs = [
    {
      headerName: "Date",
      field: "history_date",
      sortable: true,
      filter: true,
      width: 180,
      valueFormatter: (params) => {
        return params.value
          ? moment(params.value).format("YYYY-MM-DD HH:mm:ss")
          : "";
      },
    },
    {
      headerName: "Bank",
      field: "bank_name",
      sortable: true,
      filter: true,
      width: 120,
    },
    {
      headerName: "Statement Date",
      field: "statement_date",
      sortable: true,
      filter: true,
      width: 120,
      valueFormatter: (params) => {
        return params.value ? moment(params.value).format("YYYY-MM-DD") : "";
      },
    },
    {
      headerName: "Action",
      field: "history_type",
      sortable: true,
      filter: true,
      width: 100,
      cellRenderer: (params) => {
        const type = params.value;
        if (type === "+" || type === "Created") {
          return <Tag color="green">Created</Tag>;
        } else if (type === "~" || type === "Updated") {
          return <Tag color="blue">Updated</Tag>;
        } else if (type === "-" || type === "Deleted") {
          return <Tag color="red">Deleted</Tag>;
        }
        return <Tag>{type}</Tag>;
      },
    },
    {
      headerName: "User",
      field: "history_username",
      sortable: true,
      filter: true,
      width: 200,
    },
    {
      headerName: "Changes",
      field: "field_changes",
      sortable: false,
      filter: false,
      width: 150,
      cellRenderer: (params) => {
        const changes = params.value;
        if (!changes || Object.keys(changes).length === 0) {
          return <Tag color="default">No changes</Tag>;
        }
        if (changes.created) {
          return <Tag color="green">Statement created</Tag>;
        }
        return <Tag color="default">No changes</Tag>;
      },
    },
    {
      headerName: "Change Reason",
      field: "history_change_reason",
      sortable: true,
      filter: true,
      width: 150,
      valueFormatter: (params) => {
        return params.value || "N/A";
      },
    },
    {
      headerName: "Actions",
      field: "actions",
      width: 120,
      pinned: "right",
      cellRenderer: (params) => {
        return (
          <Button
            type="primary"
            icon={<RollbackOutlined />}
            size="small"
            onClick={() =>
              handleRollback(
                params.data.history_id || params.data.id,
                params.data.statement_date
              )
            }
            loading={rollbackLoading}
            disabled={rollbackLoading}
          >
            Rollback
          </Button>
        );
      },
    },
  ];

  // Default column definition
  const defaultColDef = {
    sortable: true,
    filter: true,
    resizable: true,
    minWidth: 100,
  };

  // Handle edit statement
  const handleEditStatement = (statementId) => {
    // Add edit functionality here
  };

  // Handle download statement
  const handleDownloadStatement = (statementId) => {
    // Add download functionality here
  };

  // Handle rollback to a previous version
  const handleRollback = (historyId, recordDate) => {
    Modal.confirm({
      title: "Confirm Rollback",
      content: `Are you sure you want to roll back this statement to the version from ${moment(
        recordDate
      ).format("YYYY-MM-DD HH:mm:ss")}?`,
      okText: "Yes, Roll Back",
      okType: "danger",
      cancelText: "No",
      onOk: async () => {
        try {
          setRollbackLoading(true);
          const result = await rollbackStatementHistory(historyId);
          message.success(
            result.message || "Statement rolled back successfully"
          );
          // Invalidate cache and force refresh
          invalidateCacheByPrefix("statement-history-");
          fetchAllHistory(pagination.current, pagination.pageSize, true);
        } catch (err) {
          const errorMsg =
            err.response?.data?.file ||
            err.response?.data?.error ||
            err.message;
          message.error("Failed to roll back statement: " + errorMsg);
        } finally {
          setRollbackLoading(false);
        }
      },
    });
  };

  // Format the history type for display
  const formatHistoryType = (type) => {
    switch (type) {
      case "+":
        return <Tag color="green">Created</Tag>;
      case "-":
        return <Tag color="red">Deleted</Tag>;
      case "~":
        return <Tag color="blue">Updated</Tag>;
      default:
        return <Tag>{type}</Tag>;
    }
  };

  // Format field changes for display
  const formatChanges = (fieldChanges) => {
    if (
      !fieldChanges ||
      (typeof fieldChanges === "object" &&
        Object.keys(fieldChanges).length === 0)
    ) {
      return <Tag color="gray">No changes</Tag>;
    }

    if (fieldChanges.created) {
      return <Tag color="green">Statement created</Tag>;
    }

    if (fieldChanges.deleted) {
      return <Tag color="red">Statement deleted</Tag>;
    }

    // Handle explicit rollback with no detected changes
    if (
      fieldChanges.explicit_rollback ||
      (fieldChanges.rollback && Object.keys(fieldChanges).length <= 2)
    ) {
      return <Tag color="blue">Rolled back statement</Tag>;
    }

    // Filter out metadata fields
    const displayableChanges = Object.entries(fieldChanges).filter(
      ([field]) =>
        !["rollback", "rollback_source", "explicit_rollback"].includes(field)
    );

    // If no displayable changes after filtering metadata
    if (displayableChanges.length === 0) {
      return fieldChanges.rollback ? (
        <Tag color="blue">Rolled back statement</Tag>
      ) : (
        <Tag color="gray">No changes</Tag>
      );
    }

    return (
      <Space direction="vertical">
        {displayableChanges.map(([field, { old, new: newValue }]) => {
          // Format dates
          if (field.includes("date")) {
            old = old ? moment(old).format("YYYY-MM-DD") : "None";
            newValue = newValue
              ? moment(newValue).format("YYYY-MM-DD")
              : "None";
          }

          // Format monetary values
          if (["opening_balance", "closing_balance"].includes(field)) {
            old = typeof old === "number" ? old.toFixed(2) : old;
            newValue =
              typeof newValue === "number" ? newValue.toFixed(2) : newValue;
          }

          return (
            <Tag key={field} color="blue">
              <Tooltip title={`Changed from ${old} to ${newValue}`}>
                {field.replace(/_/g, " ")}: {String(old).substring(0, 10)}
                {old && String(old).length > 10 ? "..." : ""} →{" "}
                {String(newValue).substring(0, 10)}
                {newValue && String(newValue).length > 10 ? "..." : ""}
              </Tooltip>
            </Tag>
          );
        })}
        {fieldChanges.rollback && <Tag color="blue">Rollback operation</Tag>}
      </Space>
    );
  };

  return (
    <Card
      title="Statement History"
      style={{ height: "100%" }}
      extra={
        <Space>
          <RangePicker
            value={dateRange}
            onChange={(dates) => setDateRange(dates?.[0] && dates?.[1] ? [dates[0], dates[1]] : null)}
            allowClear
            placeholder={["From", "To"]}
          />
          <Button
            icon={<ReloadOutlined />}
            onClick={() => {
              invalidateCacheByPrefix("statement-history-");
              fetchAllHistory(1, 25, true);
            }}
            loading={loading}
          >
            Refresh
          </Button>
        </Space>
      }
    >
      <Space
        direction="vertical"
        size="small"
        style={{ width: "100%", marginBottom: 16 }}
      >
        <Text type="secondary">
          Audit trail of statement create/update/delete events.{" "}
          {pagination.total} change event{pagination.total !== 1 ? "s" : ""}{" "}
          shown below.
        </Text>
      </Space>

      <div
        className="ag-theme-alpine"
        style={{ height: "600px", width: "100%" }}
      >
        <AgGridWithColumnState
          tableId="statement-history"
          columnDefs={columnDefs}
          rowData={historyData}
          getRowId={(params) =>
            String(params.data?.history_id ?? params.data?.id ?? params.rowIndex)
          }
          defaultColDef={defaultColDef}
          loading={loading}
          pagination={true}
          paginationPageSize={25}
          paginationPageSizeSelector={[10, 25, 50, 100]}
          enableRangeSelection={true}
          enableCellTextSelection={true}
          ensureDomOrder={true}
          suppressMovable={false}
          domLayout="normal"
        />
      </div>
    </Card>
  );
});
StatementHistory.displayName = "StatementHistory";

export default StatementHistory;
